import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Bot, Users, FolderKanban, Play, Crown, Building2, 
  TrendingUp, Loader2, LogOut, Settings, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [recentUsers, setRecentUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
      setStats(response.data.stats);
      setRecentUsers(response.data.recent_users);
      setTeams(response.data.teams);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#D946EF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="admin-dashboard-page">
      <div className="fixed inset-0 hero-glow" />
      
      <div className="relative z-10">
        {/* Nav */}
        <nav className="px-6 py-6 md:px-12 border-b border-white/5">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                <div className="w-10 h-10 bg-[#D946EF] rounded-sm flex items-center justify-center">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Admin Panel
                </h1>
              </div>
              <Badge className="bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20">
                Super Admin
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-white"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to App
              </Button>
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-white"
                onClick={() => navigate('/settings')}
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                className="text-red-400 hover:text-red-300"
                onClick={logout}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </nav>

        <div className="px-6 py-12 md:px-12">
          <div className="max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Platform Overview
              </h1>
              <p className="text-slate-400 mb-8">Monitor usage and manage the platform</p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card className="glass-card border-white/5">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Total Users</p>
                        <p className="text-3xl font-bold text-white">{stats.total_users || 0}</p>
                      </div>
                      <Users className="w-8 h-8 text-[#00E599]" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card border-white/5">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Teams</p>
                        <p className="text-3xl font-bold text-white">{stats.total_teams || 0}</p>
                      </div>
                      <Building2 className="w-8 h-8 text-[#D946EF]" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card border-white/5">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Test Cases</p>
                        <p className="text-3xl font-bold text-white">{stats.total_test_cases || 0}</p>
                      </div>
                      <FolderKanban className="w-8 h-8 text-[#06B6D4]" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card border-white/5">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Executions (7d)</p>
                        <p className="text-3xl font-bold text-white">{stats.executions_last_7_days || 0}</p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-[#FACC15]" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Users */}
                <Card className="glass-card border-white/5">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      <Users className="w-5 h-5 mr-2 inline text-[#00E599]" />
                      Recent Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {recentUsers.map((u) => (
                          <div key={u.user_id} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-slate-500">{u.email}</p>
                            </div>
                            <Badge className={
                              u.role === 'super_admin'
                                ? 'bg-[#D946EF]/10 text-[#D946EF]'
                                : u.role === 'admin'
                                  ? 'bg-[#00E599]/10 text-[#00E599]'
                                  : 'bg-slate-800 text-slate-300'
                            }>
                              {u.role}
                            </Badge>
                          </div>
                        ))}
                        {recentUsers.length === 0 && (
                          <p className="text-center text-slate-500 py-8">No users yet</p>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Teams */}
                <Card className="glass-card border-white/5">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      <Building2 className="w-5 h-5 mr-2 inline text-[#D946EF]" />
                      Teams
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {teams.map((team) => (
                          <div key={team.team_id} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                            <div>
                              <p className="font-medium">{team.name}</p>
                              <p className="text-sm text-slate-500">
                                Created: {new Date(team.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className="bg-slate-800 text-slate-300">
                              {team.member_count} members
                            </Badge>
                          </div>
                        ))}
                        {teams.length === 0 && (
                          <p className="text-center text-slate-500 py-8">No teams yet</p>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Platform Stats */}
              <Card className="glass-card border-white/5 mt-6">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    <Play className="w-5 h-5 mr-2 inline text-[#06B6D4]" />
                    Platform Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-black/30 rounded-lg">
                      <p className="text-4xl font-bold text-[#00E599]">{stats.total_executions || 0}</p>
                      <p className="text-sm text-slate-400 mt-1">Total Executions</p>
                    </div>
                    <div className="text-center p-4 bg-black/30 rounded-lg">
                      <p className="text-4xl font-bold text-[#D946EF]">{stats.total_test_cases || 0}</p>
                      <p className="text-sm text-slate-400 mt-1">Test Cases Created</p>
                    </div>
                    <div className="text-center p-4 bg-black/30 rounded-lg">
                      <p className="text-4xl font-bold text-[#06B6D4]">{stats.total_teams || 0}</p>
                      <p className="text-sm text-slate-400 mt-1">Active Teams</p>
                    </div>
                    <div className="text-center p-4 bg-black/30 rounded-lg">
                      <p className="text-4xl font-bold text-[#FACC15]">{stats.executions_last_7_days || 0}</p>
                      <p className="text-sm text-slate-400 mt-1">Tests Run (7 days)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
